/**
 * Data for the agency Admin Dashboard (docs/admin-dashboard.md).
 *
 * What's real today:
 *   - the team roster (teamService) and
 *   - open Command Center commands, counted per assignee — only the
 *     commands the signed-in admin can already see.
 *
 *   - monthly AI usage and the signed-in admin's own assigned commands
 *     (MyAMP /api/v1/claude-managed — both scoped to the caller).
 *
 * What's sample until the /api/admin endpoints exist [NEEDS VERIFICATION — backend]:
 *   - activity (last active, chats, areas used) and
 *   - AMS permissions.
 * Sample values are stable per person (seeded from their id) and every
 * screen that shows them says so.
 *
 * Rules are kept in this browser for now. They aren't applied to anyone's
 * session until the server stores and enforces them.
 */
import type { TeamMember } from './teamService';

// ── Permission catalog ─────────────────────────────────────────────────────

export interface PermissionDef {
  key: string;
  label: string;
  /** Words in a typed rule that point at this permission. */
  match: RegExp;
  /** Only matched when nothing more specific was. */
  generic?: boolean;
}

export const PERMISSIONS: PermissionDef[] = [
  { key: 'commissions', label: 'Commissions (reports, statements, commission columns)', match: /commission/i },
  { key: 'financials', label: 'Agency financials & billing', match: /billing|invoice|payment|financ|revenue|accounting/i },
  { key: 'clients_delete', label: 'Delete clients & policies', match: /delet|remov/i },
  { key: 'export', label: 'Export data', match: /export|csv|download (the )?data/i },
  { key: 'quotes', label: 'Quote & rate', match: /quot|\brat(e|ing)\b/i },
  { key: 'carrier_download', label: 'Carrier downloads', match: /carrier download/i },
  { key: 'calls', label: 'Calls & call recordings', match: /\bcalls?\b|recording|voice|dial/i },
  { key: 'settings', label: 'Agency settings & integrations', match: /setting|integration/i },
  { key: 'digital_agents', label: 'Create & edit Digital Agents and routines', match: /\bagents?\b|digital|routine/i },
  { key: 'reports', label: 'Agency reports', match: /reports?\b|dashboard|analytics/i, generic: true },
  { key: 'clients_view', label: 'View clients & policies', match: /client|insured|customer|polic/i, generic: true },
];
const LABEL = Object.fromEntries(PERMISSIONS.map(p => [p.key, p.label]));
export const permissionLabel = (key: string) => LABEL[key] ?? key;

// ── Stable sample values ───────────────────────────────────────────────────

function seed(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function pick<T>(list: T[], n: number, s: number): T[] {
  const out: T[] = [];
  // Callers pass s >> k, which can be negative; a negative index never matched
  // and spun this loop for hundreds of millions of turns (froze the page ~27s).
  const start = Math.abs(s) % list.length;
  for (let i = 0; out.length < Math.min(n, list.length); i++) {
    const item = list[(start + i * 7) % list.length];
    if (!out.includes(item)) out.push(item);
  }
  return out;
}

/** Sample AMS permissions — stand-in for the AMS's own per-user settings. */
export function sampleAmsPermissions(member: TeamMember, isAdminUser: boolean): Record<string, boolean> {
  const s = seed(member.id);
  const out: Record<string, boolean> = {};
  PERMISSIONS.forEach((p, i) => {
    if (isAdminUser) { out[p.key] = true; return; }
    const restricted = ['financials', 'settings', 'clients_delete'].includes(p.key)
      || (p.key === 'commissions' && s % 2 === 0)
      || (p.key === 'digital_agents' && s % 3 === 0)
      || (p.key === 'export' && s % 5 < 2);
    out[p.key] = !restricted || ((s >> i) & 15) === 0;
  });
  return out;
}

export interface MemberActivity {
  lastActiveMinutes: number;
  chats: number;
  chatTopics: string[];
  routineRuns: number;
  areasUsed: Array<{ area: string; uses: number }>;
  agentsUsed: string[];
}

const TOPICS = ['Renewals', 'COI requests', 'Endorsements', 'New quotes', 'Claims follow-up', 'Billing questions', 'Carrier appetite', 'Policy changes'];
const AREAS = ['Command Center', 'Renewal Center', 'Rate', 'Communication Center', 'Carrier Download', 'Voice', 'Submission Center', 'Digital Agents'];
const AGENTS = ['Primary agent', 'Renewals agent', 'Service agent', 'Quoting agent'];

/** Sample activity — stand-in for GET /api/admin/team-activity. */
export function sampleActivity(member: TeamMember): MemberActivity {
  const s = seed(member.id);
  const recent = [3, 12, 35, 80, 190, 600, 1500, 4300][s % 8];
  return {
    lastActiveMinutes: member.userId ? recent : -1,
    chats: member.userId ? 2 + (s % 23) : 0,
    chatTopics: member.userId ? pick(TOPICS, 3, s) : [],
    routineRuns: member.userId ? s % 14 : 0,
    areasUsed: member.userId ? pick(AREAS, 4, s >> 3).map((area, i) => ({ area, uses: 30 - i * 7 + (s % 5) })) : [],
    agentsUsed: member.userId ? pick(AGENTS, 1 + (s % 3), s >> 5) : [],
  };
}

export function lastActiveLabel(minutes: number): string {
  if (minutes < 0) return 'Never signed in';
  if (minutes < 5) return 'Active now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
}

// ── Commands (real) ────────────────────────────────────────────────────────

const OPEN_STATUS_IDS: Record<string, 'todo' | 'inprogress' | 'inreview'> = {
  '11111111-1111-1111-1111-111111111111': 'todo',
  '22222222-2222-2222-2222-222222222222': 'inprogress',
  '33333333-3333-3333-3333-333333333333': 'inreview',
};
export const COMMAND_STATUS_LABEL = { todo: 'To do', inprogress: 'In progress', inreview: 'In review' } as const;

export interface OpenCommand {
  id: string;
  code: string;
  title: string;
  status: 'todo' | 'inprogress' | 'inreview';
  /** Momentum AMP user id of the person it's with. */
  personUserId: string;
}

/** Open commands assigned to people, from the commands the admin can see. */
export async function fetchOpenPeopleCommands(): Promise<OpenCommand[]> {
  const params = new URLSearchParams({ page: '1', pageSize: '200' });
  Object.keys(OPEN_STATUS_IDS).forEach(id => params.append('statusIds', id));
  params.append('assigneeTypes', '0');
  const res = await fetch(`${process.env.REACT_APP_Auth_Url || ''}/api/momentumkanban/tasks?${params}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
  });
  if (!res.ok) throw new Error(`Couldn't load commands (${res.status}).`);
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  return list.flatMap(t => {
    const status = OPEN_STATUS_IDS[t.statusId];
    const type = Number(t.assigneeType ?? t.assignee_type ?? 0);
    if (!status || type !== 0) return [];
    // Assignee 0 means it's still with its owner.
    const assignee = Number(t.assigneeId ?? t.assignee_id ?? 0);
    const personUserId = String(assignee || t.userId || '');
    if (!personUserId) return [];
    return [{ id: String(t.id), code: t.taskNumber || String(t.id), title: t.title || '', status, personUserId }];
  });
}

// ── Rules (this browser for now) ───────────────────────────────────────────

export interface AdminRule {
  id: string;
  text: string;
  effect: 'restrict' | 'grant';
  targets: string[];
  appliesToAgents: boolean;
  status: 'active' | 'paused';
  createdAt: string;
}

const RULES_KEY = 'ig-agency-admin-rules';
const RULES_EVENT = 'ig-agency-admin-rules-changed';

function readAll(): Record<string, AdminRule[]> {
  try { return JSON.parse(localStorage.getItem(RULES_KEY) || '{}') || {}; } catch { return {}; }
}
function writeAll(all: Record<string, AdminRule[]>): void {
  try { localStorage.setItem(RULES_KEY, JSON.stringify(all)); } catch { /* best effort */ }
  window.dispatchEvent(new Event(RULES_EVENT));
}
export const subscribeToRules = (fn: () => void) => {
  window.addEventListener(RULES_EVENT, fn);
  return () => window.removeEventListener(RULES_EVENT, fn);
};
export const getRules = (memberId: string): AdminRule[] => readAll()[memberId] ?? [];
export function saveRule(memberId: string, rule: AdminRule): void {
  const all = readAll();
  const list = all[memberId] ?? [];
  const i = list.findIndex(r => r.id === rule.id);
  all[memberId] = i >= 0 ? list.map(r => (r.id === rule.id ? rule : r)) : [...list, rule];
  writeAll(all);
}
export function deleteRule(memberId: string, ruleId: string): void {
  const all = readAll();
  all[memberId] = (all[memberId] ?? []).filter(r => r.id !== ruleId);
  writeAll(all);
}

/**
 * Turn typed text into a structured rule for the admin to confirm —
 * the stand-in for POST /api/admin/members/{id}/rules/interpret.
 */
export function interpretRule(text: string): Omit<AdminRule, 'id' | 'createdAt' | 'status'> {
  const t = text.trim();
  const negative = /\b(never|don'?t|do not|no|not|block|hide|prevent|restrict|stop|can'?t|cannot|without)\b/i.test(t);
  const positive = /\b(allow|let|can|give|grant|enable|access to|show)\b/i.test(t);
  const effect: AdminRule['effect'] = positive && !negative ? 'grant' : 'restrict';
  // Neither allows nor blocks anything ("Be polite to customers"): an
  // instruction for their Digital Agents, not a permission.
  if (!negative && !positive) return { text: t, effect, targets: [], appliesToAgents: true };
  let targets = PERMISSIONS.filter(p => !p.generic && p.match.test(t)).map(p => p.key);
  if (targets.length === 0) targets = PERMISSIONS.filter(p => p.generic && p.match.test(t)).map(p => p.key);
  return { text: t, effect, targets, appliesToAgents: true };
}

/** Grants on things the AMS denies — the visible conflicts. */
export function ruleConflicts(rule: Pick<AdminRule, 'effect' | 'targets'>, ams: Record<string, boolean>): string[] {
  return rule.effect === 'grant' ? rule.targets.filter(k => ams[k] === false) : [];
}
/** Restrictions on things the AMS allows — expected, shown as "narrows". */
export function ruleNarrows(rule: Pick<AdminRule, 'effect' | 'targets'>, ams: Record<string, boolean>): string[] {
  return rule.effect === 'restrict' ? rule.targets.filter(k => ams[k] !== false) : [];
}

export interface EffectiveAccess {
  key: string;
  label: string;
  allowed: boolean;
  source: 'ams' | 'rule' | 'rule_overrides_ams';
  ruleText?: string;
}

/** AMS baseline with the active rules on top; the newest rule on a permission wins. */
export function effectiveAccess(ams: Record<string, boolean>, rules: AdminRule[]): EffectiveAccess[] {
  const active = rules.filter(r => r.status === 'active');
  return PERMISSIONS.map(p => {
    const rule = [...active].reverse().find(r => r.targets.includes(p.key));
    const base = ams[p.key] !== false;
    if (!rule) return { key: p.key, label: p.label, allowed: base, source: 'ams' };
    const allowed = rule.effect === 'grant';
    return {
      key: p.key, label: p.label, allowed,
      source: allowed && !base ? 'rule_overrides_ams' : 'rule',
      ruleText: rule.text,
    };
  });
}

// ── Monthly AI usage (real) ────────────────────────────────────────────────
// GET /api/v1/claude-managed/usage/monthly on MyAMP. Percentages only: the
// allowance amount stays on the server. Scoped to the signed-in user, so it
// gives the agency total and one person's share: the caller's, or the
// user_id's (agency owner only). The response's user_id is checked against
// the one asked for, so a mismatch is never shown under the wrong name.

export interface UsageShare { key: string; label: string; pct: number }
export interface MonthlyUsage {
  /** yyyy-MM */
  month: string;
  fromDate: string;
  toDate: string;
  /** Whose usage userPct and byItem are. */
  userId: string;
  /** That user's share of the agency allowance. */
  userPct: number;
  /** The whole agency's share used. */
  companyPct: number;
  /** How that user's own usage splits by feature — shares of userPct, adding up to 100. */
  byItem: UsageShare[];
}

const USAGE_LABEL: Record<string, string> = {
  chats: 'Digital Agent chats',
  routines: 'Routines',
  commands: 'Command Center commands',
  rate_quoting: 'Rate & quoting',
};
const usageLabel = (category: string) =>
  USAGE_LABEL[category] ?? category.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

async function myAmpGet(path: string): Promise<any> {
  const res = await fetch(`${process.env.REACT_APP_MyAMP_Url || ''}/api/v1/claude-managed${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || json?.success === false) {
    throw new Error(json?.detail || json?.message || `Request failed (${res.status}).`);
  }
  return json?.data ?? json;
}

/** Month to date by default; pass yyyy-MM for another month, and userId for someone else. */
export async function fetchMonthlyUsage(month?: string, userId?: string): Promise<MonthlyUsage> {
  const params = new URLSearchParams();
  if (month) params.set('month', month);
  if (userId) params.set('user_id', userId);
  const d = await myAmpGet(`/usage/monthly${params.toString() ? `?${params}` : ''}`);
  return {
    month: d.month,
    fromDate: d.from_date,
    toDate: d.to_date,
    userId: String(d.user_id ?? ''),
    userPct: Number(d.user_percent) || 0,
    companyPct: Number(d.company_percent) || 0,
    byItem: (d.breakdown ?? [])
      .map((b: any) => ({ key: String(b.category), label: usageLabel(String(b.category)), pct: Number(b.percentage) || 0 }))
      .sort((a: UsageShare, b: UsageShare) => b.pct - a.pct),
  };
}

// ── Agency monthly usage (real) ───────────────────────────────────────────
// GET /api/v1/claude-managed/usage/monthly/agency on MyAMP (agency owner).
// companyPct is the agency's share of its monthly allowance; byUser and
// byFeature split the agency's own usage (each adds up to 100).

export interface AgencyMonthlyUsage {
  /** yyyy-MM */
  month: string;
  fromDate: string;
  toDate: string;
  companyPct: number;
  /** user_name comes back empty, so the page names people from the roster. */
  byUser: Array<{ userId: string; name: string; pct: number }>;
  byFeature: UsageShare[];
}

/** Month to date by default; pass yyyy-MM for another month. */
export async function fetchAgencyMonthlyUsage(month?: string): Promise<AgencyMonthlyUsage> {
  const d = await myAmpGet(`/usage/monthly/agency${month ? `?month=${encodeURIComponent(month)}` : ''}`);
  return {
    month: d.month,
    fromDate: d.from_date,
    toDate: d.to_date,
    companyPct: Number(d.company_percent) || 0,
    byUser: (d.by_user ?? [])
      .map((u: any) => ({ userId: String(u.user_id ?? ''), name: String(u.user_name || '').trim(), pct: Number(u.percentage) || 0 }))
      .sort((a: { pct: number }, b: { pct: number }) => b.pct - a.pct),
    byFeature: (d.by_feature ?? [])
      .map((b: any) => ({ key: String(b.category), label: usageLabel(String(b.category)), pct: Number(b.percentage) || 0 }))
      .sort((a: UsageShare, b: UsageShare) => b.pct - a.pct),
  };
}

// ── Weekly activity per person (real) ──────────────────────────
// GET /api/v1/claude-managed/activity/weekly on MyAMP — last 7 days by
// default; user_id picks the person (agency owner only).

export interface WeeklyActivity {
  chats: number;
  routineRuns: number;
  tasks: number;
  agentsUsed: Array<{ name: string; count: number }>;
  /** Chat titles, busiest first. */
  topTopics: Array<{ id: string; title: string; count: number }>;
}

/** Last 7 days. userId (agency owner only) asks for someone else's; omitted means the caller. */
export async function fetchWeeklyActivity(userId?: string): Promise<WeeklyActivity> {
  const d = await myAmpGet(`/activity/weekly${userId ? `?user_id=${encodeURIComponent(userId)}` : ''}`);
  return {
    chats: Number(d.chats) || 0,
    routineRuns: Number(d.routine_runs) || 0,
    tasks: Number(d.tasks) || 0,
    agentsUsed: (d.agents_used ?? []).map((a: any) => ({ name: String(a.agent_name || 'Unnamed agent'), count: Number(a.count) || 0 })),
    topTopics: (d.top_topics ?? []).map((t: any) => ({
      id: String(t.conversation_id),
      title: String(t.title || 'Untitled chat').replace(/\s+/g, ' ').trim(),
      count: Number(t.count) || 0,
    })),
  };
}

// ── Commands assigned to a person (real) ─────────────────────────
// GET /api/v1/claude-managed/commands/assigned on MyAMP (limit max 100);
// user_id picks the person (agency owner only).

export interface AssignedCommand {
  id: string;
  code: string;
  title: string;
  statusLabel: string;
  /** Null for statuses that aren't open (e.g. done). */
  status: 'todo' | 'inprogress' | 'inreview' | null;
  agentName: string;
}

/** userId (agency owner only) asks for someone else's; omitted means the caller. */
export async function fetchAssignedCommands(userId?: string, limit = 100, offset = 0): Promise<{ total: number; items: AssignedCommand[] }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (userId) params.set('user_id', userId);
  const d = await myAmpGet(`/commands/assigned?${params}`);
  return {
    total: Number(d.total) || 0,
    items: (d.items ?? []).map((t: any) => ({
      id: String(t.id),
      code: t.task_number || String(t.id),
      title: t.title || '',
      statusLabel: t.status_label || '',
      status: OPEN_STATUS_IDS[t.status_id] ?? null,
      agentName: t.agent_name || '',
    })),
  };
}

/** U.S. month/day/year. */
export const usDate = (d: Date) => d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
